import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { hasRole } from "../../auth/roles.js";
import {
    createRekoAdminCase,
    deleteRekoAdminCase,
    fetchRekoAdminCases,
    fetchRekoCases,
    fetchRekoDrivers,
    fetchRekoRaces,
    fetchRekoRules,
    openBulkRekoAdminCases,
    saveRekoOpinion,
    updateRekoAdminCase,
    fetchRekoLeadOpinions,
    saveRekoLeadFinalDecision,
    publishRekoLeadFinalDecision,
    publishAllRekoLeadFinalDecisions,
    reopenRekoLeadCase,
} from "../../lib/rekoApi.js";
import CustomSelect from "../../components/CustomSelect.jsx";

const EMPTY_FORM = {
    incident_timestamp: "",
    initial_rule_id: "",
    driver_1_season_driver_id: "",
    driver_2_season_driver_id: "",
    description: "",
};

const ABSENT_REASON = "Nicht anwesend.";
const CONFLICT_SELF_REASON = "Befangen: selbst betroffen.";
const CONFLICT_TEAM_REASON = "Befangen: Teamkollege betroffen.";

function conflictReasonText(conflictType) {
    if (conflictType === "self_involved") return CONFLICT_SELF_REASON;
    if (conflictType === "teammate_involved") return CONFLICT_TEAM_REASON;
    return "Befangen.";
}

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (err instanceof Error) return err.message || "Fehler";
    return String(err);
}

function driverCarNumberValue(driver) {
    const direct =
        driver?.car_number ??
        driver?.carNumber ??
        "";

    const fromLabel = String(driver?.label || "").match(/#\s*(\d+)/);
    const raw = String(direct || fromLabel?.[1] || "")
        .replace("#", "")
        .trim();

    const number = Number.parseInt(raw, 10);

    if (Number.isFinite(number)) {
        return number;
    }

    return 999999;
}

function sortDriversByCarNumber(items) {
    return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
        const carCompare = driverCarNumberValue(a) - driverCarNumberValue(b);

        if (carCompare !== 0) return carCompare;

        return String(a?.label || "").localeCompare(String(b?.label || ""), "de", {
            numeric: true,
            sensitivity: "base",
        });
    });
}

function statusLabel(status) {
    const s = String(status || "").toLowerCase();

    if (s === "draft") return "Draft";
    if (s === "open") return "An ReKo freigegeben";
    if (s === "finalized") return "Finalisiert";
    if (s === "published") return "Veröffentlicht";
    if (s === "canceled") return "Abgebrochen";

    return status || "Unbekannt";
}

function StatusBadge({ status }) {
    const s = String(status || "").toLowerCase();

    const style = {
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "3px 9px",
        fontSize: 12,
        fontWeight: 700,
        border: "1px solid rgba(255,255,255,.12)",
    };

    if (s === "draft") {
        style.color = "#ffd470";
        style.background = "rgba(255,212,112,.10)";
    } else if (s === "open") {
        style.color = "#8ecbff";
        style.background = "rgba(142,203,255,.10)";
    } else if (s === "finalized") {
        style.color = "#ff9f9f";
        style.background = "rgba(255,120,120,.10)";
    } else if (s === "published") {
        style.color = "#8bd9a6";
        style.background = "rgba(139,217,166,.10)";
    } else if (s === "canceled") {
        style.color = "rgba(255,255,255,.72)";
        style.background = "rgba(255,255,255,.06)";
    } else {
        style.color = "var(--text)";
        style.background = "rgba(255,255,255,.06)";
    }

    return <span style={style}>{statusLabel(status)}</span>;
}

const REKO_LEAD_STATUS_OPTIONS = [
    { value: "all", label: "Alle Fälle" },
    { value: "open", label: "Open" },
    { value: "finalized", label: "Finalized" },
    { value: "published", label: "Published" },
    { value: "canceled", label: "Canceled" },
];

function rekoLeadStatusLabel(status) {
    const s = String(status || "").toLowerCase();

    if (s === "open") return "Open";
    if (s === "finalized") return "Finalized";
    if (s === "published") return "Published";
    if (s === "canceled") return "Canceled";
    if (s === "draft") return "Draft";

    return status || "Unbekannt";
}

function rekoLeadStatusPillClass(status) {
    const s = String(status || "").toLowerCase();

    if (s === "open") return "pill pill--int";
    if (s === "finalized") return "pill pill--pro";
    if (s === "published") return "pill pill--am";
    if (s === "canceled") return "pill pill--gesamt";

    return "pill";
}

function rekoLeadCaseSelectLabel(item) {
    const timestamp = item?.incident_timestamp ? ` · ${item.incident_timestamp}` : "";
    const drivers = item?.drivers?.length
        ? ` · ${item.drivers.map((driver) => driver.label).join(" / ")}`
        : "";

    return `${item.case_number} · ${rekoLeadStatusLabel(item.status)}${drivers}${timestamp}`;
}

const REKO_MEMBER_STATUS_OPTIONS = [
    { value: "all", label: "Alle Fälle" },
    { value: "open", label: "Offen" },
    { value: "finished", label: "Erledigt" },
    { value: "closed", label: "Geschlossen" },
];

function rekoMemberStatus(item) {
    const caseStatus = String(item?.status || "").toLowerCase();

    if (["finalized", "published", "canceled"].includes(caseStatus)) {
        return "closed";
    }

    if (item?.my_opinion) {
        return "finished";
    }

    return "open";
}

function rekoMemberStatusLabel(value) {
    const status = String(value || "").toLowerCase();

    if (status === "open") return "Offen";
    if (status === "finished") return "Erledigt";
    if (status === "closed") return "Geschlossen";

    return "Unbekannt";
}

function rekoMemberStatusPillClass(value) {
    const status = String(value || "").toLowerCase();

    if (status === "open") return "pill pill--int";
    if (status === "finished") return "pill pill--am";
    if (status === "closed") return "pill pill--gesamt";

    return "pill";
}

function caseDriverText(item) {
    return item?.drivers?.length
        ? item.drivers.map((driver) => driver.label).join(" / ")
        : "keine Fahrer";
}

function rekoCaseSelectLabel(item) {
    const memberStatus = rekoMemberStatus(item);
    const timestamp = item?.incident_timestamp ? ` · ${item.incident_timestamp}` : "";

    return `${item.case_number} · ${rekoMemberStatusLabel(memberStatus)} · ${caseDriverText(item)}${timestamp}`;
}

function opinionLabel(opinion) {
    if (opinion?.decision_type === "nfa") return "NFA";
    if (opinion?.decision_type === "penalty") return "Strafe";
    if (opinion?.decision_type === "absent") return "Nicht anwesend";
    if (opinion?.decision_type === "conflict") {
        if (opinion.conflict_type === "self_involved") return "Befangen: selbst";
        if (opinion.conflict_type === "teammate_involved") return "Befangen: Team";
        return "Befangen";
    }

    return opinion?.decision_type || "Unbekannt";
}

function opinionPillClass(opinion) {
    if (opinion?.decision_type === "penalty") return "pill pill--pro";
    if (opinion?.decision_type === "nfa") return "pill pill--am";
    if (opinion?.decision_type === "absent") return "pill pill--gesamt";
    return "pill pill--int";
}

function rekoMemberName(opinion) {
    return (
        opinion?.reko_driver?.display_name ||
        opinion?.reko_driver?.iracing_name ||
        `ReKo #${opinion?.reko_user_id || "?"}`
    );
}

function OpinionEditor({ item, rules, onSaved }) {
    const existing = item?.my_opinion || null;
    const existingPenalty = existing?.penalties?.[0] || null;

    const [decisionType, setDecisionType] = useState(existing?.decision_type || "");
    const [conflictType, setConflictType] = useState(existing?.conflict_type || "");
    const [reasoning, setReasoning] = useState(existing?.reasoning || "");

    const [penalizedDriverId, setPenalizedDriverId] = useState(
        existingPenalty?.penalized_season_driver_id
            ? String(existingPenalty.penalized_season_driver_id)
            : ""
    );

    const [penaltyRuleId, setPenaltyRuleId] = useState(
        existingPenalty?.rule_id
            ? String(existingPenalty.rule_id)
            : ""
    );

    const [penaltySeconds, setPenaltySeconds] = useState(
        existingPenalty?.penalty_seconds
            ? String(existingPenalty.penalty_seconds)
            : ""
    );

    const [penaltyPoints, setPenaltyPoints] = useState(
        existingPenalty?.penalty_points
            ? String(existingPenalty.penalty_points)
            : ""
    );

    const [penaltyText, setPenaltyText] = useState(existingPenalty?.penalty_text || "");
    const [busy, setBusy] = useState(false);
    const [localError, setLocalError] = useState("");
    const [localSuccess, setLocalSuccess] = useState("");

    const isLocked = item?.status !== "open";
    const drivers = Array.isArray(item?.drivers) ? item.drivers : [];
    const hasReasoningError = localError.toLowerCase().includes("begründung");

    useEffect(() => {
        const opinion = item?.my_opinion || null;
        const penalty = opinion?.penalties?.[0] || null;

        setDecisionType(opinion?.decision_type || "");
        setConflictType(opinion?.conflict_type || "");
        setReasoning(opinion?.reasoning || "");
        setPenalizedDriverId(
            penalty?.penalized_season_driver_id
                ? String(penalty.penalized_season_driver_id)
                : ""
        );
        setPenaltyRuleId(penalty?.rule_id ? String(penalty.rule_id) : "");
        setPenaltySeconds(penalty?.penalty_seconds ? String(penalty.penalty_seconds) : "");
        setPenaltyPoints(penalty?.penalty_points ? String(penalty.penalty_points) : "");
        setPenaltyText(penalty?.penalty_text || "");
        setLocalError("");
    }, [item?.id, item?.my_opinion]);

    async function submitOpinion() {
        setLocalError("");
        setLocalSuccess("");

        if (!decisionType) {
            setLocalError("Bitte NFA, Strafe, Befangenheit oder Nicht anwesend auswählen.");
            return;
        }

        if (decisionType === "conflict" && !conflictType) {
            setLocalError("Bitte Art der Befangenheit auswählen.");
            return;
        }

        if (!["absent", "conflict"].includes(decisionType) && !reasoning.trim()) {
            setLocalError("Fehlende Begründung: Bitte trage eine kurze Begründung ein.");
            return;
        }

        const autoReason =
            decisionType === "absent"
                ? ABSENT_REASON
                : decisionType === "conflict"
                    ? conflictReasonText(conflictType)
                    : "";

        const payload = {
            decision_type: decisionType,
            conflict_type: null,
            reasoning: reasoning.trim() || autoReason,
            penalty: null,
        };

        if (decisionType === "conflict") {
            payload.conflict_type = conflictType;
        }
        if (decisionType === "penalty") {
            if (!penalizedDriverId || !penaltyRuleId || !penaltySeconds || !penaltyPoints) {
                setLocalError("Bitte Fahrer, Tatbestand, Strafsekunden und Strafpunkte auswählen.");
                return;
            }

            payload.penalty = {
                penalized_season_driver_id: Number(penalizedDriverId),
                rule_id: Number(penaltyRuleId),
                penalty_seconds: Number(penaltySeconds),
                penalty_points: Number(penaltyPoints),
                penalty_text: penaltyText.trim() || null,
            };
        }

        setBusy(true);

        try {
            await saveRekoOpinion(item.id, payload);
            await onSaved?.();
            setLocalSuccess("Bewertung wurde gespeichert.");
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            style={{
                marginTop: 14,
                padding: 14,
                borderRadius: 16,
                background: existing
                    ? "rgba(139,217,166,.07)"
                    : "rgba(228,254,60,.055)",
                border: existing
                    ? "1px solid rgba(139,217,166,.24)"
                    : "1px solid rgba(228,254,60,.18)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: 12,
                }}
            >
                <strong>
                    Deine Bewertung
                </strong>

                {existing ? (
                    <span className={opinionPillClass(existing)}>
                        {opinionLabel(existing)}
                    </span>
                ) : (
                    <span className="pill pill--int">
                        Offen
                    </span>
                )}
            </div>

            {isLocked && (
                <div className="alert alert--warn" style={{ marginBottom: 12 }}>
                    Dieser Fall ist nicht mehr offen und kann aktuell nicht bewertet werden.
                </div>
            )}

            <div
                style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 12,
                }}
            >
                <button
                    type="button"
                    className={`button ${decisionType === "nfa" ? "button--primary" : "button--secondary"}`}
                    onClick={() => setDecisionType("nfa")}
                    disabled={isLocked || busy}
                >
                    NFA
                </button>

                <button
                    type="button"
                    className={`button ${decisionType === "penalty" ? "button--primary" : "button--secondary"}`}
                    onClick={() => setDecisionType("penalty")}
                    disabled={isLocked || busy}
                >
                    Strafe
                </button>

                <button
                    type="button"
                    className={`button ${decisionType === "conflict" ? "button--primary" : "button--secondary"}`}
                    onClick={() => {
                        setDecisionType("conflict");

                        if (!reasoning.trim()) {
                            setReasoning(conflictReasonText(conflictType));
                        }
                    }}
                    disabled={isLocked || busy}
                >
                    Befangenheit
                </button>

                <button
                    type="button"
                    className={`button ${decisionType === "absent" ? "button--primary" : "button--secondary"}`}
                    onClick={() => setDecisionType("absent")}
                    disabled={isLocked || busy}
                >
                    Nicht anwesend
                </button>
            </div>

            {decisionType === "absent" && (
                <div className="alert alert--warn" style={{ marginBottom: 12 }}>
                    Du markierst dass du den Fall nicht bearbeitest wegen Abwesenheit.
                </div>
            )}

            {decisionType === "conflict" && (
                <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", marginBottom: 6 }}>
                        Art der Befangenheit
                    </label>

                    <CustomSelect
                        value={conflictType}
                        onChange={(value) => {
                            setConflictType(value);
                            setReasoning(conflictReasonText(value));
                        }}
                        disabled={isLocked || busy}
                        options={[
                            { value: "self_involved", label: "Ich bin selbst betroffen" },
                            { value: "teammate_involved", label: "Teamkollege betroffen" },
                        ]}
                    />
                </div>
            )}

            {decisionType === "penalty" && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 10,
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Strafe für
                        </label>

                        <CustomSelect
                            value={penalizedDriverId}
                            onChange={setPenalizedDriverId}
                            disabled={isLocked || busy}
                            options={drivers.map((driver) => ({
                                value: driver.season_driver_id,
                                label: driver.label,
                            }))}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Tatbestand
                        </label>

                        <CustomSelect
                            value={penaltyRuleId}
                            onChange={setPenaltyRuleId}
                            disabled={isLocked || busy}
                            options={rules.map((rule) => ({
                                value: rule.id,
                                label: rule.label,
                            }))}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Strafsekunden
                        </label>

                        <CustomSelect
                            value={penaltySeconds}
                            onChange={setPenaltySeconds}
                            disabled={isLocked || busy}
                            options={[5, 10, 15, 20, 25, 30].map((v) => ({
                                value: v,
                                label: `${v} Sekunden`,
                            }))}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Strafpunkte
                        </label>

                        <CustomSelect
                            value={penaltyPoints}
                            onChange={setPenaltyPoints}
                            disabled={isLocked || busy}
                            options={[1, 2, 3].map((v) => ({
                                value: v,
                                label: `${v} Punkt${v > 1 ? "e" : ""}`,
                            }))}
                        />
                    </div>
                </div>
            )}

            {decisionType === "penalty" && (
                <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", marginBottom: 6 }}>
                        Zusatztext zur Strafe optional
                    </label>

                    <input
                        className="input"
                        style={{ width: "100%" }}
                        value={penaltyText}
                        onChange={(e) => setPenaltyText(e.target.value)}
                        disabled={isLocked || busy}
                        placeholder="z. B. Vorteil durch Kontakt, unsicheres Zurückkommen ..."
                    />
                </div>
            )}

            <div>
                <label style={{ display: "block", marginBottom: 6 }}>
                    {["absent", "conflict"].includes(decisionType) ? "Hinweis automatisch" : "Begründung"}
                </label>

                <textarea
                    className={`input ${hasReasoningError ? "reko-input--error" : ""}`}
                    rows={4}
                    style={{ width: "100%", resize: "vertical" }}
                    value={reasoning}
                    onChange={(e) => setReasoning(e.target.value)}
                    disabled={isLocked || busy}
                    placeholder={
                        decisionType === "absent"
                            ? ABSENT_REASON
                            : decisionType === "conflict"
                                ? conflictReasonText(conflictType)
                                : "Bitte kurze Begründung eintragen ..."
                    }
                />
            </div>

            {localError && (
                <div className="rv-message rv-message--error" style={{ marginTop: 12 }}>
                    {localError}
                </div>
            )}

            {localSuccess && (
                <div className="rv-message rv-message--ok" style={{ marginTop: 12 }}>
                    {localSuccess}
                </div>
            )}

            {!isLocked && (
                <div style={{ marginTop: 12 }}>
                    <button
                        type="button"
                        className="btn btn--accent"
                        onClick={submitOpinion}
                        disabled={busy}
                    >
                        {existing ? "Bewertung aktualisieren" : "Bewertung abgeben"}
                    </button>
                </div>
            )}
        </div>
    );
}

function LeadFinalizationPanel({ cases, rules, raceId, onReload }) {
    const [leadStatusFilter, setLeadStatusFilter] = useState("all");
    const [selectedCaseId, setSelectedCaseId] = useState("");
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [localError, setLocalError] = useState("");
    const [localSuccess, setLocalSuccess] = useState("");

    const [finalType, setFinalType] = useState("");
    const [finalReasoning, setFinalReasoning] = useState("");
    const [penalizedDriverId, setPenalizedDriverId] = useState("");
    const [ruleId, setRuleId] = useState("");
    const [penaltySeconds, setPenaltySeconds] = useState("");
    const [penaltyPoints, setPenaltyPoints] = useState("");
    const [penaltyText, setPenaltyText] = useState("");

    const selectedCase = detail?.case || null;
    const opinions = Array.isArray(detail?.opinions) ? detail.opinions : [];
    const finalDecision = detail?.final_decision || null;
    const drivers = Array.isArray(selectedCase?.drivers) ? selectedCase.drivers : [];
    const hasFinalReasoningError = localError.toLowerCase().includes("begründung");

    const leadStatusCounts = useMemo(() => {
        const rows = Array.isArray(cases) ? cases : [];

        return {
            all: rows.length,
            open: rows.filter((item) => item.status === "open").length,
            finalized: rows.filter((item) => item.status === "finalized").length,
            published: rows.filter((item) => item.status === "published").length,
            canceled: rows.filter((item) => item.status === "canceled").length,
        };
    }, [cases]);

    const leadStatusFilterOptions = useMemo(() => {
        return REKO_LEAD_STATUS_OPTIONS.map((option) => ({
            ...option,
            label: `${option.label} (${leadStatusCounts[option.value] ?? 0})`,
        }));
    }, [leadStatusCounts]);

    const filteredLeadCases = useMemo(() => {
        const rows = Array.isArray(cases) ? cases : [];

        if (leadStatusFilter === "all") {
            return rows;
        }

        return rows.filter((item) => String(item.status || "").toLowerCase() === leadStatusFilter);
    }, [cases, leadStatusFilter]);

    async function loadLeadDetail(caseId) {
        if (!caseId) {
            setDetail(null);
            return;
        }

        setLoading(true);
        setLocalError("");
        setLocalSuccess("");

        try {
            const data = await fetchRekoLeadOpinions(caseId);
            setDetail(data);

            const fd = data?.final_decision || null;
            const firstPenalty = fd?.penalties?.[0] || null;

            setFinalType(fd?.final_type || "");
            setFinalReasoning(fd?.final_reasoning || "");
            setPenalizedDriverId(firstPenalty?.penalized_season_driver_id ? String(firstPenalty.penalized_season_driver_id) : "");
            setRuleId(firstPenalty?.rule_id ? String(firstPenalty.rule_id) : "");
            setPenaltySeconds(
                firstPenalty?.penalty_seconds !== null && firstPenalty?.penalty_seconds !== undefined
                    ? String(firstPenalty.penalty_seconds)
                    : ""
            );
            setPenaltyPoints(
                firstPenalty?.penalty_points !== null && firstPenalty?.penalty_points !== undefined
                    ? String(firstPenalty.penalty_points)
                    : ""
            );
            setPenaltyText(firstPenalty?.penalty_text || "");
        } catch (err) {
            setLocalError(prettyError(err));
            setDetail(null);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!filteredLeadCases.length) {
            setSelectedCaseId("");
            setDetail(null);
            return;
        }

        const currentStillVisible = filteredLeadCases.some((item) => String(item.id) === String(selectedCaseId));
        if (currentStillVisible) return;

        const first =
            filteredLeadCases.find((item) => item.status === "open") ||
            filteredLeadCases.find((item) => item.status === "finalized") ||
            filteredLeadCases.find((item) => item.status === "published") ||
            filteredLeadCases[0];

        setSelectedCaseId(first?.id ? String(first.id) : "");
    }, [filteredLeadCases, selectedCaseId]);

    useEffect(() => {
        if (!selectedCaseId) return;
        loadLeadDetail(selectedCaseId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCaseId]);

    async function saveFinalDecision() {
        setLocalError("");
        setLocalSuccess("");

        if (!selectedCaseId) {
            setLocalError("Bitte zuerst einen Fall auswählen.");
            return;
        }

        if (!finalType) {
            setLocalError("Bitte finale Entscheidung auswählen.");
            return;
        }

        if (!finalReasoning.trim()) {
            setLocalError("Fehlende Begründung: Bitte trage eine finale Begründung ein.");
            return;
        }

        const payload = {
            final_type: finalType,
            final_reasoning: finalReasoning.trim(),
            penalties: [],
        };

        if (finalType === "penalty") {
            if (!penalizedDriverId || !ruleId || !penaltySeconds || !penaltyPoints) {
                setLocalError("Bitte Fahrer, Tatbestand, Strafsekunden und Strafpunkte auswählen.");
                return;
            }

            payload.penalties = [
                {
                    penalized_season_driver_id: Number(penalizedDriverId),
                    rule_id: Number(ruleId),
                    penalty_seconds: Number(penaltySeconds),
                    penalty_points: Number(penaltyPoints),
                    penalty_text: penaltyText.trim() || null,
                },
            ];
        }

        setBusy(true);

        try {
            await saveRekoLeadFinalDecision(selectedCaseId, payload);
            await loadLeadDetail(selectedCaseId);
            await onReload?.();
            setLocalSuccess("Finale Entscheidung wurde gespeichert.");
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function publishFinalDecision() {
        if (!selectedCaseId) return;

        if (!window.confirm("Finale Entscheidung wirklich veröffentlichen? Danach ist sie für Fahrer sichtbar.")) {
            return;
        }

        setBusy(true);
        setLocalError("");
        setLocalSuccess("");

        try {
            await publishRekoLeadFinalDecision(selectedCaseId);
            await loadLeadDetail(selectedCaseId);
            await onReload?.();
            setLocalSuccess("Finale Entscheidung wurde veröffentlicht.");
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function publishAllFinalizedDecisions() {
        const count = Number(leadStatusCounts.finalized || 0);

        if (!raceId || count <= 0) return;

        if (!window.confirm(`${count} gespeicherte finale Entscheidung(en) jetzt gemeinsam veröffentlichen? Danach werden Results, Lizenzpunkte und Standings neu berechnet.`)) {
            return;
        }

        setBusy(true);
        setLocalError("");
        setLocalSuccess("");

        try {
            const data = await publishAllRekoLeadFinalDecisions({ raceId });
            await onReload?.();

            if (selectedCaseId) {
                await loadLeadDetail(selectedCaseId);
            }

            setLocalSuccess(`${data?.published_cases ?? count} finale Entscheidung(en) wurden gemeinsam veröffentlicht.`);
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function reopenCase() {
        if (!selectedCaseId) return;

        if (!window.confirm("Case wirklich wieder öffnen?")) {
            return;
        }

        setBusy(true);
        setLocalError("");
        setLocalSuccess("");

        try {
            await reopenRekoLeadCase(selectedCaseId);
            await loadLeadDetail(selectedCaseId);
            await onReload?.();
            setLocalSuccess("Case wurde wieder geöffnet.");
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="card reko-role-card reko-role-card--final" style={{ display: "grid", gap: 16 }}>
            <div>
                <h2>Finalisierung</h2>
                <p className="muted">
                    ReKo Lead Bereich: Bewertungen ansehen, finale Entscheidung speichern und veröffentlichen.
                </p>
            </div>

            <div
                style={{
                    display: "grid",
                    gap: 12,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                    }}
                >
                    <span className="pill pill--int">
                        Open: {leadStatusCounts.open}
                    </span>

                    <span className="pill pill--pro">
                        Finalized: {leadStatusCounts.finalized}
                    </span>

                    <div>
                        <button
                            type="button"
                            className="btn btn--accent"
                            onClick={publishAllFinalizedDecisions}
                            disabled={busy || !raceId || Number(leadStatusCounts.finalized || 0) <= 0}
                        >
                            Alle gespeicherten Entscheidungen veröffentlichen ({leadStatusCounts.finalized})
                        </button>
                    </div>

                    <span className="pill pill--am">
                        Published: {leadStatusCounts.published}
                    </span>

                    <span className="pill pill--gesamt">
                        Canceled: {leadStatusCounts.canceled}
                    </span>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 12,
                        alignItems: "end",
                    }}
                >
                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Status
                        </label>

                        <CustomSelect
                            value={leadStatusFilter}
                            onChange={(value) => {
                                setLeadStatusFilter(value || "all");
                                setSelectedCaseId("");
                                setDetail(null);
                                setLocalError("");
                                setLocalSuccess("");
                            }}
                            allowEmpty={false}
                            options={leadStatusFilterOptions}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Case auswählen
                        </label>

                        <div className="rv-wide-select">
                            <CustomSelect
                                value={selectedCaseId}
                                onChange={setSelectedCaseId}
                                disabled={!filteredLeadCases.length}
                                allowEmpty={false}
                                placeholder="Kein Case verfügbar"
                                options={filteredLeadCases.map((item) => ({
                                    value: item.id,
                                    label: rekoLeadCaseSelectLabel(item),
                                }))}
                            />
                        </div>
                    </div>
                </div>

                {Array.isArray(cases) && cases.length > 0 && !filteredLeadCases.length && (
                    <div className="muted">
                        Für diesen Status gibt es aktuell keine Cases.
                    </div>
                )}
            </div>

            {localError && (
                <div className="rv-message rv-message--error">
                    {localError}
                </div>
            )}

            {localSuccess && (
                <div className="rv-message rv-message--ok">
                    {localSuccess}
                </div>
            )}

            {loading && (
                <div className="muted">
                    Lade Bewertungen …
                </div>
            )}

            {!loading && selectedCase && (
                <>
                    <div
                        style={{
                            border: "1px solid rgba(255,255,255,.10)",
                            borderRadius: 16,
                            padding: 14,
                            background: "rgba(255,255,255,.035)",
                        }}
                    >
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                            <strong>{selectedCase.case_number}</strong>
                            <StatusBadge status={selectedCase.status} />
                            {selectedCase.incident_timestamp && (
                                <span className="muted">Timestamp: {selectedCase.incident_timestamp}</span>
                            )}
                        </div>

                        <div style={{ marginTop: 8 }}>
                            <strong>Fahrer:</strong>{" "}
                            {drivers.length
                                ? drivers.map((d) => d.label).join(" / ")
                                : "keine Fahrer hinterlegt"}
                        </div>

                        <div style={{ marginTop: 4 }}>
                            <strong>Tatbestand:</strong>{" "}
                            {selectedCase.initial_rule?.label || "nicht gesetzt"}
                        </div>

                        {selectedCase.description && (
                            <div className="muted" style={{ marginTop: 8 }}>
                                {selectedCase.description}
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: 12,
                        }}
                    >
                        {opinions.length === 0 && (
                            <div className="muted">
                                Noch keine ReKo-Bewertungen vorhanden.
                            </div>
                        )}

                        {opinions.map((opinion) => (
                            <div
                                key={opinion.id}
                                style={{
                                    border: "1px solid rgba(255,255,255,.10)",
                                    borderRadius: 16,
                                    padding: 14,
                                    background: "rgba(255,255,255,.035)",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        flexWrap: "wrap",
                                        marginBottom: 8,
                                    }}
                                >
                                    <strong>
                                        {rekoMemberName(opinion)}
                                    </strong>

                                    <span className={opinionPillClass(opinion)}>
                                        {opinionLabel(opinion)}
                                    </span>
                                </div>

                                <div className="muted" style={{ whiteSpace: "pre-wrap" }}>
                                    {opinion.reasoning}
                                </div>

                                {opinion.penalties?.length > 0 && (
                                    <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
                                        {opinion.penalties.map((p) => (
                                            <div key={p.id}>
                                                <strong>{p.driver_name || "Fahrer"}</strong>{" "}
                                                · {p.penalty_seconds}s · {p.penalty_points} SP
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div
                        style={{
                            padding: 14,
                            borderRadius: 16,
                            background: "rgba(228,254,60,.055)",
                            border: "1px solid rgba(228,254,60,.18)",
                        }}
                    >
                        <h3 style={{ marginTop: 0 }}>Finale Entscheidung</h3>

                        {finalDecision && (
                            <div className="alert alert--ok" style={{ marginBottom: 12 }}>
                                Finale Entscheidung ist gespeichert.
                                {selectedCase.status === "published" ? " Dieser Case ist veröffentlicht." : " Veröffentlichung steht noch aus."}
                            </div>
                        )}

                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                            <button
                                type="button"
                                className={`button ${finalType === "nfa" ? "button--primary" : "button--secondary"}`}
                                onClick={() => setFinalType("nfa")}
                                disabled={busy || selectedCase.status === "published"}
                            >
                                NFA
                            </button>

                            <button
                                type="button"
                                className={`button ${finalType === "penalty" ? "button--primary" : "button--secondary"}`}
                                onClick={() => setFinalType("penalty")}
                                disabled={busy || selectedCase.status === "published"}
                            >
                                Strafe
                            </button>
                        </div>

                        {finalType === "penalty" && (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                    gap: 10,
                                    marginBottom: 12,
                                }}
                            >
                                <div>
                                    <label style={{ display: "block", marginBottom: 6 }}>Strafe für</label>
                                    <CustomSelect
                                        value={penalizedDriverId}
                                        onChange={setPenalizedDriverId}
                                        disabled={busy || selectedCase.status === "published"}
                                        options={drivers.map((driver) => ({
                                            value: driver.season_driver_id,
                                            label: driver.label,
                                        }))}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: "block", marginBottom: 6 }}>Tatbestand</label>

                                    <div className="rv-wide-select rv-rule-select">
                                        <CustomSelect
                                            value={ruleId}
                                            onChange={setRuleId}
                                            disabled={busy || selectedCase.status === "published"}
                                            options={rules.map((rule) => ({
                                                value: rule.id,
                                                label: rule.label,
                                            }))}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: "block", marginBottom: 6 }}>Strafsekunden</label>
                                    <CustomSelect
                                        value={penaltySeconds}
                                        onChange={setPenaltySeconds}
                                        disabled={busy || selectedCase.status === "published"}
                                        options={[0, 5, 10, 15, 20, 25, 30].map((v) => ({
                                            value: v,
                                            label: `${v} Sekunden`,
                                        }))}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: "block", marginBottom: 6 }}>Strafpunkte</label>
                                    <CustomSelect
                                        value={penaltyPoints}
                                        onChange={setPenaltyPoints}
                                        disabled={busy || selectedCase.status === "published"}
                                        options={[1, 2, 3].map((v) => ({
                                            value: v,
                                            label: `${v} Punkt${v > 1 ? "e" : ""}`,
                                        }))}
                                    />
                                </div>
                            </div>
                        )}

                        {finalType === "penalty" && (
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ display: "block", marginBottom: 6 }}>
                                    Zusatztext optional
                                </label>

                                <input
                                    className="input"
                                    style={{ width: "100%" }}
                                    value={penaltyText}
                                    onChange={(e) => setPenaltyText(e.target.value)}
                                    disabled={busy || selectedCase.status === "published"}
                                    placeholder="z. B. Vorteil durch Kontakt ..."
                                />
                            </div>
                        )}

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Finale Begründung
                            </label>

                            <textarea
                                className={`input ${hasFinalReasoningError ? "reko-input--error" : ""}`}
                                rows={4}
                                style={{ width: "100%", resize: "vertical" }}
                                value={finalReasoning}
                                onChange={(e) => setFinalReasoning(e.target.value)}
                                disabled={busy || selectedCase.status === "published"}
                                placeholder="Finale Begründung für Fahreransicht ..."
                            />
                        </div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                            {selectedCase.status !== "published" && (
                                <button
                                    type="button"
                                    className="btn btn--accent"
                                    onClick={saveFinalDecision}
                                    disabled={busy}
                                >
                                    Finale Entscheidung speichern
                                </button>
                            )}

                            {(selectedCase.status === "finalized" || selectedCase.status === "published") && (
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={reopenCase}
                                    disabled={busy}
                                >
                                    Wieder öffnen
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default function RekoIncidents() {
    const { user } = useAuth();

    const canAdmin = hasRole(user, "admin");
    const canOrga = hasRole(user, "orga");
    const canManageIncidents = canAdmin || canOrga;
    const canReko = canAdmin || hasRole(user, "reko") || hasRole(user, "reko_lead");
    const canLead = canAdmin || hasRole(user, "reko_lead");

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const [season, setSeason] = useState(null);
    const [races, setRaces] = useState([]);
    const [raceId, setRaceId] = useState("");

    const [drivers, setDrivers] = useState([]);
    const [rules, setRules] = useState([]);

    const [adminCases, setAdminCases] = useState([]);
    const [rekoCases, setRekoCases] = useState([]);

    const [rekoStatusFilter, setRekoStatusFilter] = useState("all");
    const [selectedRekoCaseId, setSelectedRekoCaseId] = useState("");

    const [editingCaseId, setEditingCaseId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const sortedDrivers = useMemo(() => {
        return sortDriversByCarNumber(drivers);
    }, [drivers]);

    const selectedRace = useMemo(() => {
        return races.find((r) => String(r.id) === String(raceId)) || null;
    }, [races, raceId]);

    const draftCount = useMemo(() => {
        return adminCases.filter((c) => c.status === "draft").length;
    }, [adminCases]);

    const rekoStatusCounts = useMemo(() => {
        const counts = {
            all: rekoCases.length,
            open: 0,
            finished: 0,
            closed: 0,
        };

        for (const item of rekoCases) {
            const status = rekoMemberStatus(item);
            counts[status] = (counts[status] || 0) + 1;
        }

        return counts;
    }, [rekoCases]);

    const rekoStatusFilterOptions = useMemo(() => {
        return REKO_MEMBER_STATUS_OPTIONS.map((option) => ({
            ...option,
            label: `${option.label} (${rekoStatusCounts[option.value] ?? 0})`,
        }));
    }, [rekoStatusCounts]);

    const filteredRekoCases = useMemo(() => {
        if (rekoStatusFilter === "all") return rekoCases;

        return rekoCases.filter((item) => rekoMemberStatus(item) === rekoStatusFilter);
    }, [rekoCases, rekoStatusFilter]);

    const selectedRekoCase = useMemo(() => {
        return filteredRekoCases.find((item) => String(item.id) === String(selectedRekoCaseId)) || null;
    }, [filteredRekoCases, selectedRekoCaseId]);

    useEffect(() => {
        if (!canReko) return;

        if (!filteredRekoCases.length) {
            setSelectedRekoCaseId("");
            return;
        }

        const currentStillVisible = filteredRekoCases.some((item) => String(item.id) === String(selectedRekoCaseId));
        if (currentStillVisible) return;

        const firstOpen =
            filteredRekoCases.find((item) => rekoMemberStatus(item) === "open") ||
            filteredRekoCases[0];

        setSelectedRekoCaseId(firstOpen?.id ? String(firstOpen.id) : "");
    }, [canReko, filteredRekoCases, selectedRekoCaseId]);

    async function loadBaseData() {
        setLoading(true);
        setError("");
        setSuccessMsg("");

        try {
            const raceData = await fetchRekoRaces({ seriesKey: "gt3x" });
            const driverData = await fetchRekoDrivers({ seriesKey: "gt3x" });
            const ruleData = await fetchRekoRules();

            setSeason(raceData.season || null);
            setRaces(Array.isArray(raceData.races) ? raceData.races : []);
            setDrivers(Array.isArray(driverData.drivers) ? driverData.drivers : []);
            setRules(Array.isArray(ruleData.rules) ? ruleData.rules : []);

            const initialRaceId =
                raceData.current_race_id ||
                raceData.races?.[0]?.id ||
                "";

            setRaceId(String(initialRaceId || ""));
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setLoading(false);
        }
    }

    async function loadCases(nextRaceId = raceId) {
        if (!nextRaceId) {
            setAdminCases([]);
            setRekoCases([]);
            return;
        }

        setError("");

        try {
            if (canManageIncidents) {
                const data = await fetchRekoAdminCases({ raceId: nextRaceId });
                setAdminCases(Array.isArray(data.cases) ? data.cases : []);
            } else {
                setAdminCases([]);
            }

            if (canReko) {
                const data = await fetchRekoCases({ raceId: nextRaceId });
                setRekoCases(Array.isArray(data.cases) ? data.cases : []);
            }
        } catch (err) {
            setError(prettyError(err));
        }
    }

    useEffect(() => {
        loadBaseData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!raceId) return;
        loadCases(raceId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raceId, canManageIncidents, canReko]);

    function updateForm(key, value) {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    function resetForm() {
        setEditingCaseId(null);
        setForm(EMPTY_FORM);
    }

    function startEdit(item) {
        const involved = Array.isArray(item.drivers) ? item.drivers : [];

        setError("");
        setSuccessMsg("");
        setEditingCaseId(item.id);
        setForm({
            incident_timestamp: item.incident_timestamp || "",
            initial_rule_id: item.initial_rule_id ? String(item.initial_rule_id) : "",
            driver_1_season_driver_id: involved[0]?.season_driver_id ? String(involved[0].season_driver_id) : "",
            driver_2_season_driver_id: involved[1]?.season_driver_id ? String(involved[1].season_driver_id) : "",
            description: item.description || "",
        });
    }

    async function saveDraft() {
        if (!raceId) {
            setError("Bitte zuerst ein Rennen auswählen.");
            setSuccessMsg("");
            return;
        }

        if (!form.driver_1_season_driver_id && !form.driver_2_season_driver_id) {
            setError("Bitte mindestens einen Fahrer auswählen.");
            setSuccessMsg("");
            return;
        }

        setBusy(true);
        setError("");
        setSuccessMsg("");

        const payload = {
            race_id: Number(raceId),
            incident_timestamp: form.incident_timestamp.trim() || null,
            initial_rule_id: form.initial_rule_id ? Number(form.initial_rule_id) : null,
            driver_1_season_driver_id: form.driver_1_season_driver_id ? Number(form.driver_1_season_driver_id) : null,
            driver_2_season_driver_id: form.driver_2_season_driver_id ? Number(form.driver_2_season_driver_id) : null,
            description: form.description.trim() || null,
        };

        try {
            if (editingCaseId) {
                await updateRekoAdminCase(editingCaseId, payload);
            } else {
                await createRekoAdminCase(payload);
            }

            resetForm();
            await loadCases(raceId);
            setSuccessMsg(editingCaseId ? "Incident wurde gespeichert." : "Incident wurde als Draft gespeichert.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function deleteDraft(caseId) {
        if (!window.confirm("Draft wirklich löschen?")) return;

        setBusy(true);
        setError("");
        setSuccessMsg("");

        try {
            await deleteRekoAdminCase(caseId);
            await loadCases(raceId);
            setSuccessMsg("Draft wurde gelöscht.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function openBulk() {
        if (!raceId) return;

        if (!window.confirm(`${draftCount} Draft(s) wirklich an die ReKo freigeben?`)) {
            return;
        }

        setBusy(true);
        setError("");
        setSuccessMsg("");

        try {
            await openBulkRekoAdminCases({ raceId: Number(raceId) });
            await loadCases(raceId);
            setSuccessMsg(`${draftCount} Draft(s) wurden an die ReKo freigegeben.`);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    if (loading) {
        return (
            <div className="card reko-modern-page">
                <h1>ReKo Incidents</h1>
                <p className="muted">Lade Daten …</p>
            </div>
        );
    }

    return (
        <div className="reko-modern-page">
            <section className="reko-page-hero">
                <div className="reko-page-hero__inner">
                    <div>
                        <div className="reko-page-hero__eyebrow">
                            RaceVision Race Control
                        </div>

                        <h1>ReKo Incidents</h1>

                        <p className="muted">
                            Zentrale Erfassung und spätere Bewertung der RaceVision Incidents.
                            Die Bereiche sind nach Orga, ReKo und Finalisierung getrennt.
                        </p>
                    </div>

                    <div className="reko-page-hero__meta">
                        <div>
                            <label className="rv-dd rt-filter-item">
                                <span className="rv-dd__label">Rennen</span>

                                <CustomSelect
                                    value={raceId}
                                    onChange={(nextRaceId) => {
                                        setRaceId(nextRaceId);
                                        setError("");
                                        setSuccessMsg("");
                                        setSelectedRekoCaseId("");
                                        setRekoStatusFilter("all");
                                        resetForm();
                                    }}
                                    allowEmpty={false}
                                    buttonClassName="rv-select--race"
                                    options={races.map((race) => ({
                                        value: race.id,
                                        label: race.label,
                                    }))}
                                />
                            </label>
                        </div>
                        {season && (
                            <div style={{ alignSelf: "end", opacity: 0.85 }}>
                                Saison: <strong>{season.display_name}</strong>
                            </div>
                        )}

                        {selectedRace && (
                            <div style={{ alignSelf: "end", opacity: 0.85 }}>
                                Aktuell gewählt: <strong>{selectedRace.label}</strong>
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="rv-message rv-message--error" style={{ marginTop: 14 }}>
                        {error}
                    </div>
                )}

                {successMsg && (
                    <div className="rv-message rv-message--ok" style={{ marginTop: 14 }}>
                        {successMsg}
                    </div>
                )}
            </section>

            {canManageIncidents && (
                <div className="card reko-role-card reko-role-card--orga">
                    <h2>{editingCaseId ? "Incident bearbeiten" : "Neuen Incident erfassen"}</h2>

                    <p className="muted">
                        Die Case-Nummer wird automatisch erzeugt, z. B. R3-01.
                        Incidents bleiben zuerst als Draft und werden gesammelt an die ReKo freigegeben.
                    </p>

                    <div
                        style={{
                            marginTop: 16,
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 12,
                        }}
                    >
                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Timestamp ingame
                            </label>

                            <input
                                className="input"
                                value={form.incident_timestamp}
                                onChange={(e) => updateForm("incident_timestamp", e.target.value)}
                                placeholder="z. B. 00:14:32"
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Tatbestand Regelwerk
                            </label>

                            <div className="rv-wide-select rv-rule-select">
                                <CustomSelect
                                    value={form.initial_rule_id}
                                    onChange={(value) => updateForm("initial_rule_id", value)}
                                    options={rules.map((rule) => ({
                                        value: rule.id,
                                        label: rule.label,
                                    }))}
                                />
                            </div>
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Fahrer 1
                            </label>

                            <div className="rv-wide-select">
                                <CustomSelect
                                    value={form.driver_1_season_driver_id}
                                    onChange={(value) => updateForm("driver_1_season_driver_id", value)}
                                    options={sortedDrivers.map((driver) => ({
                                        value: driver.season_driver_id,
                                        label: driver.label,
                                    }))}
                                />
                            </div>
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Fahrer 2
                            </label>

                            <div className="rv-wide-select">
                                <CustomSelect
                                    value={form.driver_2_season_driver_id}
                                    onChange={(value) => updateForm("driver_2_season_driver_id", value)}
                                    options={sortedDrivers.map((driver) => ({
                                        value: driver.season_driver_id,
                                        label: driver.label,
                                    }))}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Beschreibung / Orga-Notiz
                        </label>

                        <textarea
                            className="input"
                            rows={4}
                            style={{ width: "100%", resize: "vertical" }}
                            value={form.description}
                            onChange={(e) => updateForm("description", e.target.value)}
                            placeholder="Kurze Beschreibung des Vorfalls …"
                        />
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
                        <button
                            className="btn btn--accent"
                            type="button"
                            onClick={saveDraft}
                            disabled={busy}
                        >
                            {editingCaseId ? "Änderungen speichern" : "Als Draft speichern"}
                        </button>

                        {editingCaseId && (
                            <button
                                className="btn"
                                type="button"
                                onClick={resetForm}
                                disabled={busy}
                            >
                                Bearbeitung abbrechen
                            </button>
                        )}
                    </div>
                </div>
            )}

            {canManageIncidents && (
                <div className="card reko-role-card reko-role-card--orga">
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}
                    >
                        <div>
                            <h2>Gesammelte Incidents</h2>
                            <p className="muted">
                                Nach Sichtung der Orga, alle Vorfälle mit dem Button an die Reko freigeben.
                            </p>
                        </div>

                        <button
                            className="btn btn--accent"
                            type="button"
                            onClick={openBulk}
                            disabled={busy || draftCount <= 0}
                        >
                            Alle Vorfälle an ReKo freigeben ({draftCount})
                        </button>
                    </div>

                    <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                        {!adminCases.length && (
                            <div className="muted">
                                Noch keine Incidents für dieses Rennen erfasst.
                            </div>
                        )}

                        {adminCases.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    border: "1px solid rgba(255,255,255,.10)",
                                    borderRadius: 14,
                                    padding: 14,
                                    background: "rgba(255,255,255,.035)",
                                    display: "grid",
                                    gap: 8,
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 10,
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                                        <strong>{item.case_number}</strong>
                                        <StatusBadge status={item.status} />
                                        {item.incident_timestamp && (
                                            <span className="muted">
                                                Timestamp: {item.incident_timestamp}
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        {["draft", "open"].includes(item.status) && (
                                            <button className="btn" type="button" onClick={() => startEdit(item)} disabled={busy}>
                                                Bearbeiten
                                            </button>
                                        )}

                                        {item.status === "draft" && (
                                            <button className="btn" type="button" onClick={() => deleteDraft(item.id)} disabled={busy}>
                                                Löschen
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <strong>Fahrer:</strong>{" "}
                                    {item.drivers?.length
                                        ? item.drivers.map((d) => d.label).join(" / ")
                                        : "keine Fahrer hinterlegt"}
                                </div>

                                <div>
                                    <strong>Tatbestand:</strong>{" "}
                                    {item.initial_rule?.label || "nicht gesetzt"}
                                </div>

                                {item.description && (
                                    <div className="muted">
                                        {item.description}
                                    </div>
                                )}

                            </div>
                        ))}
                    </div>
                </div>
            )}

            {canReko && (
                <div className="card reko-role-card reko-role-card--reko">
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap",
                            alignItems: "flex-start",
                        }}
                    >
                        <div>
                            <h2>ReKo Mitglied - Meine Fälle</h2>
                            <p className="muted">
                                Wähle einen Fall aus. Offen bedeutet noch nicht bewertet, erledigt bedeutet deine Bewertung ist gespeichert.
                            </p>
                        </div>

                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span className="pill pill--int">
                    Offen: {rekoStatusCounts.open}
                </span>
                            <span className="pill pill--am">
                    Erledigt: {rekoStatusCounts.finished}
                </span>
                            <span className="pill pill--gesamt">
                    Geschlossen: {rekoStatusCounts.closed}
                </span>
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: 16,
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                            gap: 12,
                            alignItems: "end",
                        }}
                    >
                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Status
                            </label>

                            <CustomSelect
                                value={rekoStatusFilter}
                                onChange={(value) => {
                                    setRekoStatusFilter(value || "all");
                                    setSelectedRekoCaseId("");
                                }}
                                allowEmpty={false}
                                options={rekoStatusFilterOptions}
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Case auswählen
                            </label>

                            <div className="rv-wide-select">
                                <CustomSelect
                                    value={selectedRekoCaseId}
                                    onChange={setSelectedRekoCaseId}
                                    disabled={!filteredRekoCases.length}
                                    allowEmpty={false}
                                    placeholder="Kein Fall verfügbar"
                                    options={filteredRekoCases.map((item) => ({
                                        value: item.id,
                                        label: rekoCaseSelectLabel(item),
                                    }))}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 16 }}>
                        {!rekoCases.length && (
                            <div className="muted">
                                Keine aktuell freigegebenen ReKo-Fälle für dieses Rennen.
                            </div>
                        )}

                        {rekoCases.length > 0 && !filteredRekoCases.length && (
                            <div className="muted">
                                Für diesen Status gibt es aktuell keine Fälle.
                            </div>
                        )}

                        {selectedRekoCase && (
                            <div
                                style={{
                                    border: "1px solid rgba(255,255,255,.10)",
                                    borderRadius: 14,
                                    padding: 14,
                                    background: "rgba(255,255,255,.035)",
                                    display: "grid",
                                    gap: 8,
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 10,
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                                        <strong>{selectedRekoCase.case_number}</strong>
                                        <StatusBadge status={selectedRekoCase.status} />

                                        <span className={rekoMemberStatusPillClass(rekoMemberStatus(selectedRekoCase))}>
                                {rekoMemberStatusLabel(rekoMemberStatus(selectedRekoCase))}
                            </span>

                                        {selectedRekoCase.incident_timestamp && (
                                            <span className="muted">
                                    Timestamp: {selectedRekoCase.incident_timestamp}
                                </span>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <strong>Fahrer:</strong>{" "}
                                    {caseDriverText(selectedRekoCase)}
                                </div>

                                <div>
                                    <strong>Tatbestand:</strong>{" "}
                                    {selectedRekoCase.initial_rule?.label || "nicht gesetzt"}
                                </div>

                                {selectedRekoCase.description && (
                                    <div className="muted">
                                        {selectedRekoCase.description}
                                    </div>
                                )}

                                <OpinionEditor
                                    item={selectedRekoCase}
                                    rules={rules}
                                    onSaved={() => loadCases(raceId)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {canLead && (
                <LeadFinalizationPanel
                    cases={rekoCases.length ? rekoCases : adminCases.filter((c) =>
                        ["open", "finalized", "published", "canceled"].includes(c.status)
                    )}
                    rules={rules}
                    raceId={raceId}
                    onReload={() => loadCases(raceId)}
                />
            )}

        </div>
    );
}
